import { FC } from "react";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { useMe } from "../../hooks/use-me";
import { ReactSVG } from "react-svg";
import styled from "styled-components";
import { MaxWidthSpan, BtnSpinnerAutoLoading } from "visible-ui";
import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/auth-hooks";
import { useRecoilCallback } from "recoil";
import { surveyAnswersMap, currentSurveyAtom } from "../../state/atoms/survey-responses-atoms";
import { flatQuestionsSurveySelector } from "../../state/selectors/survey-selector";
import SurveyResponsesClient from "../../communication/clients/survey-client"
import { toastError } from "visible-ui";
import Papa from 'papaparse';
import { Button } from "react-bootstrap";

const LayoutStyled = styled.section`
  min-height: 75vh;
`;

export interface SurveySubmittedOrEndedScreenProps {
  mode: "submitted" | "ended";
}

const SurveySubmittedOrEndedScreen: FC<SurveySubmittedOrEndedScreenProps> = ({
  mode,
}) => {
  const { t } = useTranslation();
  const { me, loading } = useMe();
  const { getAccessToken } = useAuth();
  const [showEditResponsesBtn, setShowEditResponsesBtn] = useState(false)
  const [showDownloadResponsesBtn, setShowDownloadResponsesBtn] = useState(false)

  useEffect(() => {
    if (!loading) {
      let hasFullAccess = !(me?.sub_permissions)?.length
      setShowEditResponsesBtn(hasFullAccess)
      setShowDownloadResponsesBtn(hasFullAccess)
    }
  }, [me, loading])

  const updateSurvey = useRecoilCallback(
    ({ set }) =>
      async () => {
        try {
          const accessToken = await getAccessToken();
          const currentSurvey = await SurveyResponsesClient.Instance.updateSurvey(accessToken, false)
          set(currentSurveyAtom, currentSurvey);
        }
        catch (e) {
          toastError(t("errorMessage.reopenSurvey"));
        }
      },
    [getAccessToken]
  );


  const flatQuestions = useRecoilValue(flatQuestionsSurveySelector);
  const { answersMap } = useRecoilValue(surveyAnswersMap);

  const exportToCSV = () => {
    const data = flatQuestions.map(question => {
      const answer = answersMap.get(question.id) || {
        value: '',
        reason_to_not_answer: '',
        will_not_answer: false,
      };
      return {
        question: question.question,
        answer: answer.value,
        reason_to_not_answer: answer.reason_to_not_answer,
        will_not_answer: answer.will_not_answer,
      };
    });
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    // eslint-disable-next-line scanjs-rules/assign_to_href
    link.href = url;
    link.setAttribute('download', `moodys2027cyberaisurvey_${me?.organization_name}_responses.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <LayoutStyled className="d-flex flex-column align-items-center justify-content-center">
      {mode === "submitted" && (
        <section className="rounded-circle p-5 bg-white text-primary mb-5">
          <ReactSVG
            className="p-2"
            src={process.env.PUBLIC_URL + "assets/img/email-mail-post-card.svg"}
          />
        </section>
      )}
      <div className="fs-1 mb-3">
        {mode === "submitted"
          ? t("submitSurvey.successfullySubmitted")
          : t("submitSurvey.theSurveyHasEnded")}
      </div>
      <MaxWidthSpan
        maxWidth="350px"
        className="fs-2 text-center text-gray-700 fw-lighter"
      >
        {mode === "submitted" ? (
          <>
            <div>
              {t("submitSurvey.thankForTakingTime")}
            </div>
            {showEditResponsesBtn && (
              <div>
                <BtnSpinnerAutoLoading
                  handleClick={async (onStart, onDone) => {
                    onStart();
                    await updateSurvey();
                    onDone();
                  }}
                  loadMsg={t("submitSurvey.reopenSurvey")}
                  className="fs-3 px-5 mt-3"
                >
                  {t("submitSurvey.editResponses")}
                </BtnSpinnerAutoLoading>
              </div>
            )}
            {showDownloadResponsesBtn && (
              <div>
                <BtnSpinnerAutoLoading
                  handleClick={async (onStart, onDone) => {
                    onStart();
                    exportToCSV();
                    onDone();
                  }}
                  loadMsg={t("submitSurvey.reopenSurvey")}
                  className="fs-3 px-5 mt-3"
                >
                  {t("submitSurvey.downloadResponses")}
                </BtnSpinnerAutoLoading>
              </div>
            )}
            <div>
              <Button
                onClick={() => {
                  window.open("https://service.bitsighttech.com/app/static/pages/access-request?token=ac6f0d96-99a6-44f0-a208-6d51cb6fc35d", '_blank', 'noreferrer');
                }}
                className="fs-3 px-5 mt-3"
                >
                {t("submitSurvey.ecaLink")}
              </Button>
            </div>
          </>
        ) : (
          <>
            {t("submitSurvey.thankAndHope")}
            <br />
          </>
        )}
      </MaxWidthSpan>
    </LayoutStyled>
  );
};

export default SurveySubmittedOrEndedScreen;
